import { AxiosError } from "axios"

import ApiService from "./ApiService"
import { ArticleType, ArticleSearchScope, ApiServiceName, ApiLinkRel, ApiLinkEntity, Purpose, AggregateBy, ErrorCode } from "config/constants"
import { Guid, IMultipleEntitiesResponse, IApiLink, IMultipleEntities, IApiFollowLink, ArticleNumber, ArticleAny, ITranslations } from "@/types"
import { IGetArticleDto } from "models/Article"
import { IArticleShortDTO, ArticleShort } from "models/ArticleShort"
import { ArticleMachineExt, ICreateArticleMachineExtDto, IGetArticleMachineExtDto, IUpdateArticleMachineExtDto } from "models/ArticleMachineExt"
import { ArticleEquipment, ICreateArticleEquipmentDto, IGetArticleEquipmentDto, IUpdateArticleEquipmentDTO } from "models/ArticleEquipment"
import ArticleAsset, { IArticleAssetDto } from "models/ArticleAsset"
import ExtendAsSelfPopulatable from "./extend/ExtendAsSelfPopulatable"
import CustomError from "models/CustomError"
import { isGuidEmpty } from "utils/utils"
import AssetArticle, { IAssetArticleDto } from "models/AssetArticle"
import { hasMorePages } from "./utils"
import ProductHierarchyProductLine, { IGetProductHierarchyProductLineDto } from "models/ProductHierarchyProductLine"
import ProductModelArticle, { IGetProductModelArticleDto } from "models/ProductModelArticle"
import { ArticleMachineInt, ICreateArticleMachineIntDto, IGetArticleMachineIntDto, IUpdateArticleMachineIntDto } from "models/ArticleMachineInt"
import { AssetOwnershipType, AssetStatus } from "models/Asset"
import { ArticleZts, ICreateArticleZtsDTO, IGetArticleZtsDto, IUpdateArticleZtsDto } from "models/ArticleZts"
import { prepareTranslations } from "utils/translations"
import { AssetStoredStatus } from 'models/AssetDisplayStatus';
import ArticleClassInfo, { ArticleClass, IArticleClassInfoDto } from "models/ArticleClassInfo"
import ErrorHandler from "utils/errorHandler"

export interface IGetArticleAssetsParams {
    articleIds?: Guid[]
    articleNumbers?: string[]
    assetIds?: Guid[]
    purposes?: Purpose[]
    lineIds?: Guid[]
    familyIds?: Guid[]
    modelIds?: Guid[]
    yearOfConstructions?: number[]
    emissionLevels?: string[]
    inventoryNumbers?: string[]
    serialNumbers?: string[]
    serialNumbersMachine?: string[]
    manfucaturerCodes?: string[]
    criteria?: string
    scope?: ArticleSearchScope
    descriptionAsc?: boolean
    isOnlyWithLocation?: boolean
    drawingNumbers?: string[]
    isLabeled?: boolean
    labels?: string[]
    isOperational?: boolean
    assetOwnershipType?: AssetOwnershipType
    profitCenterIds?: (Guid | null)[]
    internalOrderNumbers?: string[]
    supplierOrderNumbers?: string[]
    statuses?: AssetStatus[]
    homebaseIds?: (Guid | null)[] // null for "no value"
    warehouseIds?: (Guid | null)[] // null for "no value"
    colorIds?: (Guid | null)[] // null for "no special paint"
    pageNumber?: number
    pageSize?: number
    stateOfStorage?: AssetStoredStatus
    isOnlyForHolder?: false
    articleClasses?: ArticleClass[]
    conditionFrom?: number
    conditionTo?: number
    initialCondition?: boolean
    availabilityDateFrom?: string
    availabilityDateTo?: string
    lastScanDateFrom?: string
    lastScanDateTo?: string
    lengthFrom?: number
    lengthTo?: number
    widthFrom?: number
    widthTo?: number
    heightFrom?: number
    heightTo?: number
    volumeFrom?: number
    volumeTo?: number
    quickCouplingSystems?: string[]
    connectionTypes?: string[]
    teeth?: boolean
    cuttingEdge?: boolean
    heavyDuty?: boolean
    locking?: string[]
    controlSystems?: string[]
    agility?: string[]
    referenceNumbersSale?: string[]
    referenceNumbersRental?: string[]
    assetTypes?: string[]
}

class ArticleService {

    async getArticles(params: {
        criteria?: string
        scope?: ArticleSearchScope
        isOnlyForHolder?: boolean
        isExtManufacturer?: boolean
        manufacturer?: string
        manufacturerArticleNo?: string
        pageNumber?: number
        pageSize?: number
        excludeArticleIds?: Guid[]
    }): Promise<IMultipleEntities<ArticleShort> | null> {
        const {
            scope,
            criteria,
            isOnlyForHolder,
            isExtManufacturer,
            manufacturer,
            manufacturerArticleNo,
            pageNumber = 1,
            pageSize = 10,
            excludeArticleIds = [],
        } = params
        try {
            const query: IApiFollowLink["query"] = {
                Criteria: criteria || undefined,
                Scope: scope,
                IsOnlyForHolder: isOnlyForHolder,
                IsExtManufacturer: isExtManufacturer,
                Manufacturer: manufacturer || undefined,
                ManufacturerArticleNo: manufacturerArticleNo || undefined,
                PageNumber: pageNumber,
                PageSize: pageSize,
            }

            const data: IMultipleEntitiesResponse<IArticleShortDTO> = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.article,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                }
            })

            return {
                list: data.entities.filter(dto => !excludeArticleIds.includes(dto.id)).map((dto: IArticleShortDTO) => ArticleShort.fromDTO(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getArticles({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response && error.response.status === 422) {
                    throw new CustomError({
                        messageCode: "common.invalidSearchString"
                    })
                }
            }
        }

        return null
    }

    async getArticle(articleIdOrNumber: Guid | ArticleNumber): Promise<ArticleAny | null> {
        try {
            const dto: IGetArticleDto = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.article,
                    rel: ApiLinkRel.self,
                    params: {
                        id: articleIdOrNumber
                    }
                }
            })

            switch (dto.articleType) {
                case ArticleType.machine:
                    if (dto.isExtManufacturer) {
                        return new ArticleMachineExt(dto as IGetArticleMachineExtDto)
                    } else {
                        return new ArticleMachineInt(dto as IGetArticleMachineIntDto)
                    }
                case ArticleType.equipment:
                    return new ArticleEquipment(dto as IGetArticleEquipmentDto)
                case ArticleType.zts:
                    return new ArticleZts(dto as IGetArticleZtsDto)
            }
        } catch (e) { }
        return null
    }

    async getArticleClasses(): Promise<ArticleClassInfo[]> {
        try {
            const data: IArticleClassInfoDto[] = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.articleClass,
                    rel: ApiLinkRel.all
                }
            })

            return data.map(d => new ArticleClassInfo(d))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getProductModelArticles(params: {
        link: IApiLink
        pageSize?: number
        pageNumber?: number
    }): Promise<IMultipleEntities<ProductModelArticle>> {
        try {
            const data: IMultipleEntitiesResponse<IGetProductModelArticleDto> = await ApiService.followLink({
                link: params.link,
                linkQuery: {
                    PageSize: params.pageSize,
                    PageNumber: params.pageNumber
                },
            })

            return {
                list: data.entities.map(dto => new ProductModelArticle(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getProductModelArticles({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getArticleAssets(params: IGetArticleAssetsParams): Promise<IMultipleEntities<ArticleAsset> | null> {
        try {
            const data: IMultipleEntitiesResponse<IArticleAssetDto> = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.articleAsset,
                    rel: ApiLinkRel.all
                },
                data: {
                    ...params,
                    aggregateBy: AggregateBy.article
                }
            })

            return {
                list: data.entities.map((dto: IArticleAssetDto) => ArticleAsset.fromDto(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getArticleAssets({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
        }

        return null
    }

    async getAssetArticles(
        params: IGetArticleAssetsParams,
        { excludeAssetIds = [], signal }: { excludeAssetIds?: Guid[], signal?: AbortSignal } = {}
    ): Promise<IMultipleEntities<AssetArticle>> {
        try {
            const data: IMultipleEntitiesResponse<IAssetArticleDto> = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.articleAsset,
                    rel: ApiLinkRel.all
                },
                data: {
                    ...params,
                    aggregateBy: AggregateBy.asset
                },
                signal,
            })

            return {
                list: (excludeAssetIds.length ? data.entities.filter(e => !excludeAssetIds.includes(e.assetId)) : data.entities).map((dto: IAssetArticleDto) => new AssetArticle(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetArticles({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }, { excludeAssetIds }) : undefined,
            }
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response && error.response.status === 422) {
                    throw new CustomError({
                        messageCode: "common.invalidSearchString"
                    })
                }
            }
            ErrorHandler.handleHttpError(e)
        }
    }

    async createArticle(dto: ICreateArticleMachineExtDto | ICreateArticleMachineIntDto | ICreateArticleEquipmentDto | ICreateArticleZtsDTO): Promise<Guid> {
        try {
            const resp: { id: Guid } = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.article,
                    rel: ApiLinkRel.new
                },
                data: dto
            })
            return resp.id
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async updateArticle(link: IApiLink, data: IUpdateArticleEquipmentDTO | IUpdateArticleMachineExtDto | IUpdateArticleMachineIntDto | IUpdateArticleZtsDto): Promise<Guid> {
        try {
            const resp: { id: Guid } = await ApiService.followLink({
                link,
                data,
            })
            return resp.id
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    createArticleZts(articleShort: ArticleShort): Promise<Guid | null> {
        return this.createArticle({
            articleType: articleShort.articleType,
            isExtManufacturer: false,
            manufacturerDescription: articleShort.manufacturerDescription,
            manufacturerArticleNo: articleShort.manufacturerArticleNo,
            description: articleShort.description,
        })
    }

    async getArticleId(articleShort: ArticleShort): Promise<Guid | null> {
        let articleId: string | null = articleShort.id

        if (isGuidEmpty(articleId)) {
            if (articleShort.articleType === ArticleType.zts) {
                articleId = await this.createArticleZts(articleShort)
            } else {
                return null
            }
        }

        return articleId
    }

    async getProductHierarchy(params?: {
        isExtManufacturer?: boolean
    }): Promise<ProductHierarchyProductLine[]> {
        try {
            const data: IGetProductHierarchyProductLineDto[] = await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.productHierarchy,
                    rel: ApiLinkRel.all,
                    query: {
                        isExtManufacturer: params?.isExtManufacturer,
                    }
                },
            })

            return data.map(d => new ProductHierarchyProductLine(d))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async updateProductHierarchy(id: Guid, descriptionTranslations: ITranslations): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.productHierarchy,
                    rel: ApiLinkRel.update,
                    params: { id }
                },
                data: {
                    descriptionTranslations: prepareTranslations(descriptionTranslations),
                }
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async #createProductHierarchy(params: {
        descriptionTranslations: ITranslations
        parentId?: Guid
        isExtManufacturer?: boolean
    }): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.article,
                followLink: {
                    entity: ApiLinkEntity.productHierarchy,
                    rel: ApiLinkRel.new,
                },
                data: {
                    ...params,
                    descriptionTranslations: prepareTranslations(params.descriptionTranslations),
                }
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createProductLine(params: {
        descriptionTranslations: ITranslations
    }): Promise<void> {
        await this.#createProductHierarchy(params)
    }

    async createProductFamily(params: {
        descriptionTranslations: ITranslations
        parentId: Guid
    }): Promise<void> {
        await this.#createProductHierarchy(params)
    }

    async createProductModel(params: {
        descriptionTranslations: ITranslations
        parentId: Guid
        isExtManufacturer: boolean
    }): Promise<void> {
        await this.#createProductHierarchy(params)
    }

    async updateArticleInspectionSettings(link: IApiLink, data: { inspectionRequired: boolean }): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }

    }
}

const ArticleServiceExtent = ExtendAsSelfPopulatable(ArticleService, ApiServiceName.article)

export default new ArticleServiceExtent()
